/* eslint-disable max-len */
import { useContext } from 'react'

import { generateApplicationUrl, launchModal } from '@domain-group/javascript-utils'
import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { mapToState } from '@functions/campaign'
import { mergeAndStringifyEntityObjects } from '@functions/entity'

import PageContext from '@contexts/pageContext'

import * as campaignActions from '@redux/modules/campaign'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

import defaultState, { requiredFields } from '@models/campaign'

import type {
  AgencyModel, BrandModel, CampaignCategoryModel, CampaignCommercialCategoryModel, CampaignModel,
  CampaignRuralCategoryModel, CampaignSaleMethodModel, OrderItemModel, PortalModel, ProviderModel, TeamModel, UserModel,
} from '@models/types'

const stringifyArrayKeys = ['data_store_item_values']
const stringifyObjectKeys: (keyof CampaignModel)[] = ['data', 'extra_fields']

export type GenerateUrlsOptions = {
  adCopyTypes?: string[],
  artworkIds?: number[],
  imageIds?: number[],
  prefixRoute?: string,
}

export const generateUrls = (
  campaign: Partial<CampaignModel> = {},
  options: GenerateUrlsOptions = {},
) => {
  const { id, public_token } = campaign
  const {
    adCopyTypes, artworkIds, imageIds, prefixRoute = 'systemManager',
  } = options

  const addAccountingItemModalPayload = encodeURIComponent(
    JSON.stringify({ campaign: { id } }),
  )

  const hasAdCopyTypes = adCopyTypes && adCopyTypes.length > 0

  return {
    adCopyQueryParams: hasAdCopyTypes ? `&adCopyTypes=${encodeURIComponent(JSON.stringify(adCopyTypes))}` : '',
    addCampaignAccountingItemUrl: `#/campaigns/${id}/edit/accounting?showModal=AddAccountingItemModal&showModalPayload=${addAccountingItemModalPayload}`,
    agencyMarketingCampaignsIndexUrl: `#/${prefixRoute}/campaigns?type=agency`,
    archivedCampaignsIndexUrl: `#/${prefixRoute}/campaigns?hidden=true`,
    artworkReviewerPublicUrl: `#/public/campaigns/${id}/artworkReviewer?publicToken=${public_token}&artworkIds=${encodeURIComponent(JSON.stringify(artworkIds))}`,
    artworkReviewerUrl: `#/public/campaigns/${id}/artworkReviewer?artworkIds=${encodeURIComponent(JSON.stringify(artworkIds))}`,
    campaignAccountingUrl: `#/campaigns/${id}/edit/accounting`,
    campaignAuctionOverviewUrl: `${prefixRoute}/auctions`,
    campaignEditDashboardUrl: `#/campaigns/${id}/edit`,
    campaignEditDetailsUrl: `#/campaigns/${id}/edit/details`,
    campaignMediaProviderUrl: `#/${prefixRoute}/campaigns/${id}/edit/media`,
    campaignCopywritingProviderUrl: `#/${prefixRoute}/campaigns/${id}/edit/copywriting`,
    campaignMediaUrl: `#/campaigns/${id}/edit/media`,
    campaignOverviewUrl: `#/campaigns/${id}/edit`,
    campaignPortalsUrl: `#/campaigns/${id}/edit/portals`,
    campaignQuotesUrl: `#/campaigns/${id}/edit/quotes`,
    campaignsIndexUrl: `#/${prefixRoute}/campaigns?hidden=false`,
    editCampaignUrl: `#/${prefixRoute}/campaigns/${id}/edit`,
    mediaReviewerAdCopyPublicUrl: `#/public/campaigns/${id}/mediaReviewer?publicToken=${public_token}`,
    mediaReviewerPublicUrl: `#/public/campaigns/${id}/mediaReviewer?publicToken=${public_token}&imageIds=${encodeURIComponent(JSON.stringify(imageIds))}`,
    propertyMarketingCampaignsIndexUrl: `#/${prefixRoute}/campaigns?type=property`,
    realhubTrainingDashboardIndex: '/training/dashboard#/',
  }
}

type CreateCampaignParams = {
  campaignParams: Partial<CampaignModel>,
  dispatch: AppDispatch,
}

const createCampaign = (params: CreateCampaignParams) => {
  const { campaignParams, dispatch } = params
  const { createCampaign: createFn } = campaignActions

  // @ts-expect-error TS2345
  return dispatch(createFn(campaignParams)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { id } = data

      const { campaignEditDetailsUrl } = generateUrls({ id })

      return {
        success,
        data,
        redirectUrl: campaignEditDetailsUrl,
      }
    }

    return response
  })
}

type UpdateCampaignPayload = {
  campaign: CampaignModel,
  campaignParams: CampaignModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const updateCampaign = (params: UpdateCampaignPayload) => {
  const {
    campaign, campaignParams, dispatch, requestOptions,
  } = params

  const { updateCampaign: updateFn } = campaignActions

  const updatedParams = mergeAndStringifyEntityObjects(
    campaign,
    campaignParams,
    stringifyObjectKeys,
    // @ts-expect-error TS2345
    stringifyArrayKeys,
  )

  return dispatch(updateFn(updatedParams, requestOptions))
}

type PortalSyncPayload = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
}

const portalSync = (params: PortalSyncPayload) => {
  const {
    campaign, dispatch,
  } = params

  const { portalSync: syncFn } = campaignActions

  return dispatch(syncFn(campaign))
}

type BulkRemapPortalsPayload = {
  dispatch: AppDispatch,
  fileData: File,
  requestOptions: EntityOptions,
}

const bulkPortalRemap = (params: BulkRemapPortalsPayload) => {
  const { dispatch, fileData, requestOptions } = params
  const { bulkPortalRemap: bulkRemapFn } = campaignActions

  return dispatch(bulkRemapFn(fileData, requestOptions))
}

type BulkRemapCampaignPortalsParams = {
  showBulkRemapCampaignPortalsModal: Function,
}

const bulkRemapCampaignPortals = (params: BulkRemapCampaignPortalsParams) => new Promise((resolve, reject) => {
  const { showBulkRemapCampaignPortalsModal } = params

  if (showBulkRemapCampaignPortalsModal){
    showBulkRemapCampaignPortalsModal({})

    resolve({ success: true, result: {} })
  }

  reject(new Error('showBulkRemapCampaignPortalsModal not defined in PageContext callbacks'))
})

type CheckRequiredFieldsParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
}

const checkRequiredFields = (params: CheckRequiredFieldsParams) => {
  const { campaign, dispatch } = params
  const { checkRequiredFields: checkFn } = campaignActions

  return dispatch(checkFn(campaign))
}

type ToggleHiddenParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
};

const toggleHidden = (props: ToggleHiddenParams) => {
  const { campaign, dispatch, requestOptions } = props

  const { toggleHidden: toggleFn } = campaignActions

  return dispatch(toggleFn(campaign, requestOptions))
}

type SyncToCanvaParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
};

const syncToCanva = (props: SyncToCanvaParams) => {
  const { campaign, dispatch, requestOptions } = props

  const { syncToCanva: syncFn } = campaignActions

  return dispatch(syncFn(campaign, requestOptions))
}

type CheckImageExportSizesParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
}

const checkImageExportSizes = (params: CheckImageExportSizesParams) => {
  const { campaign, dispatch } = params
  const { checkImageExportSizes: checkFn } = campaignActions

  return dispatch(checkFn(campaign))
}

type TogglePublishedParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
};

const togglePublished = (props: TogglePublishedParams) => {
  const { campaign, dispatch } = props
  const { published } = campaign

  const { togglePublished: toggleFn } = campaignActions

  if (published){
    return dispatch(toggleFn(campaign))
  }

  return checkRequiredFields({ campaign, dispatch }).then((response) => {
    const { success } = response

    if (success){
      return dispatch(toggleFn(campaign))
    }

    return response
  })
}

export type EmailParams = {
  redirect_url: string,
  message: string,
  recipients: { email: string, is_custom: boolean, user_id: number }[],
  complete_selection?: boolean,
}

type EmailDashboardLinkParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  emailParams: EmailParams,
  requestOptions?: EntityOptions,
}

const emailDashboardLink = (params: EmailDashboardLinkParams) => {
  const {
    campaign, dispatch, emailParams, requestOptions,
  } = params

  const { emailDashboardLink: emailFn } = campaignActions

  return dispatch(emailFn(campaign, emailParams, requestOptions))
}

export type ShareParams = {
  activity_key?: string,
  message: string,
  recipients: { user_id: number, mobile: string }[],
  redirect_url: string,
}

type ShareMobileLinkParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
  shareParams: ShareParams,
}

const shareMobileLink = (params: ShareMobileLinkParams) => {
  const {
    campaign, dispatch, requestOptions, shareParams,
  } = params

  const { shareMobileLink: shareFn } = campaignActions

  return dispatch(shareFn(campaign, shareParams, requestOptions))
}

type ShareEmailOrMobileParams = {
  activityKey?: string,
  endPoint?: string,
  linkAnchor: string,
  preSelectedUsers?: (UserModel | undefined)[],
  completeSelection?: boolean,
}

type ShareProject = {
  message: string,
  user_ids: number[],
}

type ShareProjectParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
  shareProjectParams: ShareProject,
}

const shareProject = (params: ShareProjectParams) => {
  const {
    campaign, dispatch, requestOptions, shareProjectParams,
  } = params

  const { shareProject: shareFn } = campaignActions

  // @ts-expect-error TS2345
  return dispatch(shareFn(campaign, shareProjectParams, requestOptions))
}

export type DownloadParams = {
  image_ids: number[],
  resize?: string,
  skip_watermark?: boolean,
}

type DownloadImagesParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  downloadParams: DownloadParams,
  requestOptions?: EntityOptions,
}

const downloadImages = (params: DownloadImagesParams) => {
  const {
    campaign, dispatch, downloadParams, requestOptions,
  } = params

  const { downloadImages: downloadFn } = campaignActions

  return dispatch(downloadFn(campaign, downloadParams, requestOptions))
}

type CreatePublicTokenParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createPublicToken = (params: CreatePublicTokenParams) => {
  const {
    campaign, dispatch, requestOptions,
  } = params

  const { createPublicToken: createFn } = campaignActions

  return dispatch(createFn(campaign, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export type MergeParams = {
  archive_source_campaign: boolean,
  destination_campaign_id: number,
  merge_entity_keys: string[],
}

type MergeCampaignsParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  mergeParams: MergeParams,
  requestOptions?: EntityOptions,
}

const mergeCampaigns = (params: MergeCampaignsParams) => {
  const {
    campaign, dispatch, mergeParams, requestOptions,
  } = params

  const { mergeCampaigns: mergeFn } = campaignActions
  return dispatch(mergeFn(campaign, mergeParams, requestOptions)).then((response) => {
    const { success } = response
    const { destination_campaign_id } = mergeParams

    if (success){
      const { data } = response
      const { campaignOverviewUrl } = generateUrls({ id: destination_campaign_id })

      return {
        redirectUrl: campaignOverviewUrl,
        success,
        data,
      }
    }

    return response
  })
}

type BuildPublicMediaReviewerAdCopyUrlParams = {
  campaign: CampaignModel,
}

const buildPublicMediaReviewerUrl = (params: BuildPublicMediaReviewerAdCopyUrlParams) => {
  const { campaign } = params

  const { public_token } = campaign || {}

  if (!public_token) return ''

  const { mediaReviewerAdCopyPublicUrl } = generateUrls(campaign)

  // @ts-expect-error TS2339
  const { applicationUrlWithHash } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
    hash: mediaReviewerAdCopyPublicUrl,
  })

  return applicationUrlWithHash
}

export function useCampaignForm(
  campaign: Partial<CampaignModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignForm = useForm(
    defaultState,
    {
      entity: mapToState(campaign),
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [campaign.id, campaign.cache_key],
  )

  return {
    ...campaignForm,
  }
}

export type UseCampaignFormPayload = ReturnType<typeof useCampaignForm>

export function useRelations(campaign: Partial<CampaignModel>) {
  const {
    agency_id,
    campaign_category_id,
    campaign_commercial_category_id,
    campaign_rural_category_id,
    campaign_sale_method_id,
    team_id,
  } = campaign || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    agencies,
    brands,
    campaignCategories,
    campaignCommercialCategories,
    campaignRuralCategories,
    campaignSaleMethods,
    portals,
    teams,
  } = entities

  const agency: Partial<AgencyModel> = agency_id ? agencies[agency_id] || {} : {}
  const brand: Partial<BrandModel> = agency?.brand_id ? brands[agency.brand_id] || {} : {}
  const campaignSaleMethod: Partial<CampaignSaleMethodModel> = campaign_sale_method_id
    ? campaignSaleMethods[campaign_sale_method_id] || {} : {}

  const campaignCategory: Partial<CampaignCategoryModel> = campaign_category_id && campaignCategories[campaign_category_id]
    ? campaignCategories[campaign_category_id] : {}
  const campaignCommercialCategory: Partial<CampaignCommercialCategoryModel> = campaign_commercial_category_id
    ? campaignCommercialCategories[campaign_commercial_category_id] : {}
  const campaignRuralCategory: Partial<CampaignRuralCategoryModel> = campaign_rural_category_id
    && campaignRuralCategories[campaign_rural_category_id]
    ? campaignRuralCategories[campaign_rural_category_id] : {}

  const incomingPortalData = campaign.data?.portal || {}
  const incomingPortal: Partial<PortalModel> = portals[incomingPortalData.id] || {}

  const team: Partial<TeamModel> = team_id ? teams[team_id] || {} : {}

  return {
    agency,
    brand,
    campaignCategory,
    campaignCommercialCategory,
    campaignRuralCategory,
    campaignSaleMethod,
    incomingPortal,
    team,
  }
}

type Options = {
  generateUrlsOptions?: GenerateUrlsOptions,
  performHttpRequests?: boolean,
  requestOptions?: object,
  skipLoadedForKeysCheck?: boolean,
}

function useCampaign(initEntity: Partial<CampaignModel> = {}, options: Options = {}) {
  const {
    generateUrlsOptions, performHttpRequests, requestOptions, skipLoadedForKeysCheck,
  } = options

  // @ts-expect-error TS2322
  const { entity: campaign }: { entity: CampaignModel } = useLatestEntity(initEntity, 'campaigns')
  const { id } = campaign

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showBulkRemapCampaignPortalsModal } = callbacks || {}

  const {
    creating, loaded, loading, syncing, updating,
  } = useSelector(reduxState => reduxState.campaigns)

  const {
    agency, brand, campaignCategory, campaignCommercialCategory, campaignRuralCategory, incomingPortal, team,
  } = useRelations(campaign)

  useReduxAction(
    'campaigns',
    'loadCampaign',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: () => !!performHttpRequests,
      skipLoadedForKeysCheck,
    },
  )

  const campaignStartDate = campaign.dates?.start.date
  const campaignUniqueId = campaign.data?.unique_id || null
  const crmSyncApi = agency.options?.crm_sync_api || null
  const vendorAmlComplete = campaign?.vendor_aml_complete ?? false
  const isPropertyMarketing = campaign?.agency_marketing === false

  return {
    agency,
    brand,
    callbacks: {
      addCampaignItem: () => launchModal({
        callbacks,
        modalKey: 'NewCampaignItemModal',
        payload: { campaign },
      }),
      assignCampaignAgents: () => launchModal({
        callbacks,
        modalKey: 'AssignCampaignAgentsModal',
        payload: { campaign },
      }),
      bulkRemapCampaignPortals: () => bulkRemapCampaignPortals({ showBulkRemapCampaignPortalsModal }),
      bulkPortalRemap: (fileData: File, entityOptions: EntityOptions) => (
        bulkPortalRemap({ fileData, dispatch, requestOptions: entityOptions })
      ),
      bulkSelectImages: () => launchModal({
        callbacks,
        modalKey: 'BulkSelectImagesModal',
        payload: {
          callbacks: {
            updateEntity: (campaignParams: CampaignModel, entityOptions: EntityOptions) => (
              updateCampaign({
                campaign, campaignParams, dispatch, requestOptions: entityOptions,
              })
            ),
          },
          entity: campaign,
        },
      }),
      copyAdCopyLink: () => launchModal({
        callbacks,
        modalKey: 'CopyLinkModal',
        payload: { campaign, link: buildPublicMediaReviewerUrl({ campaign }) },
      }),
      createCampaign: (
        campaignParams: Partial<CampaignModel>,
      ) => createCampaign({ campaignParams, dispatch }),
      createCampaignModal: () => launchModal({
        callbacks,
        modalKey: 'CreateCampaignModal',
        payload: { campaign },
      }),
      newCampaignModal: (customOptions?: Record<string, boolean>) => launchModal({
        callbacks,
        modalKey: 'NewCampaignModal',
        payload: { ...customOptions, campaign },
      }),
      checkImageExportSizes: () => checkImageExportSizes({ campaign, dispatch }),
      checkRequiredFields: () => checkRequiredFields({ campaign, dispatch }),
      createPublicToken: (entityOptions?: EntityOptions) => createPublicToken({
        campaign, dispatch, requestOptions: entityOptions,
      }),
      downloadImages: (downloadParams: DownloadParams, entityOptions?: EntityOptions) => downloadImages({
        campaign, dispatch, downloadParams, requestOptions: entityOptions,
      }),
      downloadMedia: (selectedImageIds: number[]) => launchModal({
        callbacks,
        modalKey: 'DownloadMediaModal',
        payload: { campaign, selectedImageIds },
      }),
      editCampaignPortalDataModal: () => launchModal({
        callbacks,
        modalKey: 'EditCampaignPortalDataModal',
        payload: { campaign },
      }),
      editCampaignDates: (payload: { callbacks: { successFn: (start_date?: string) => void } }) => launchModal({
        callbacks,
        modalKey: 'EditCampaignDatesModal',
        payload: { ...payload, campaign },
      }),
      emailDashboardLink: (emailParams: EmailParams, entityOptions?: EntityOptions) => (
        emailDashboardLink({
          campaign, dispatch, emailParams, requestOptions: entityOptions,
        })
      ),
      importCampaign: () => launchModal({
        callbacks,
        modalKey: 'ImportCampaignModal',
        payload: { campaign },
      }),
      manageCampaignTestStatusModal: () => launchModal({
        callbacks,
        modalKey: 'ManageCampaignTestStatusModal',
        payload: { campaign },
      }),
      managePortalDataDetailedFeatures: () => launchModal({
        callbacks,
        modalKey: 'ManagePortalDataDetailedFeaturesModal',
        payload: { campaign },
      }),
      managePrioritySettings: () => launchModal({
        callbacks,
        modalKey: 'ManageCampaignPrioritySettingsModal',
        payload: { campaign },
      }),
      mergeCampaigns: (
        mergeParams: MergeParams,
        entityOptions: EntityOptions,
      ) => mergeCampaigns({
        campaign, dispatch, mergeParams, requestOptions: entityOptions,
      }),
      mergeCampaignsModal: () => launchModal({
        callbacks,
        modalKey: 'MergeCampaignsModal',
        payload: { campaign },
      }),
      reviewAdCopy: (orderItem: Partial<OrderItemModel>, provider: Partial<ProviderModel>) => launchModal({
        callbacks,
        modalKey: 'AdCopyReviewerModal',
        payload: { campaign, orderItem, provider },
      }),
      reviewMedia: (provider: ProviderModel) => launchModal({
        callbacks,
        modalKey: 'MediaReviewerModal',
        payload: { campaign, provider },
      }),
      portalSync: () => portalSync({ campaign, dispatch }),
      shareEmailLink: (shareEmailParams: ShareEmailOrMobileParams) => launchModal({
        callbacks,
        modalKey: 'SelectEmailRecipientModal',
        payload: {
          campaign,
          defaultSubject: 'Media Selection',
          ...shareEmailParams,
        },
      }),
      shareMobileLink: (shareParams: ShareParams, entityOptions?: EntityOptions) => (
        shareMobileLink({
          campaign, dispatch, requestOptions: entityOptions, shareParams,
        })
      ),
      shareProject: (shareProjectParams: ShareProject, entityOptions?: EntityOptions) => (
        shareProject({
          campaign, dispatch, requestOptions: entityOptions, shareProjectParams,
        })
      ),
      shareProjectModal: (provider: ProviderModel, orderItem?: OrderItemModel) => launchModal({
        callbacks,
        modalKey: 'ShareProjectModal',
        payload: { campaign, provider, orderItem },
      }),
      selectSMSRecipient: (shareParams: ShareEmailOrMobileParams) => launchModal({
        callbacks,
        modalKey: 'SelectSMSRecipientModal',
        payload: {
          campaign,
          defaultSubject: 'Media Selection',
          ...shareParams,
        },
      }),
      syncToCanva: (entityOptions?: EntityOptions) => syncToCanva(
        { campaign, dispatch, requestOptions: entityOptions },
      ),
      toggleHidden: (entityOptions?: EntityOptions) => toggleHidden(
        { campaign, dispatch, requestOptions: entityOptions },
      ),
      togglePublished: () => togglePublished({ campaign, dispatch }),
      updateCampaign: (campaignParams: CampaignModel, entityOptions: EntityOptions) => (
        updateCampaign({
          campaign, campaignParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    campaignCategory,
    campaignCommercialCategory,
    campaignRuralCategory,
    campaignStartDate,
    campaignUniqueId,
    creating,
    crmSyncApi,
    incomingPortal,
    isPropertyMarketing,
    vendorAmlComplete,
    loaded,
    loading,
    syncing,
    team,
    updating,
    urls: generateUrls(campaign, generateUrlsOptions),
  }
}

export type UseCampaignPayload = ReturnType<typeof useCampaign>

export default useCampaign
